/**
 * Locate the scorecard payload inside an interview plan.
 *
 * The plan endpoint can emit the scorecard in two shapes:
 *
 *   1. Flat `scorecard_config: { questions, is_feedback_mandatory,
 *      feedback_description, rating_description, ... }` — returned by
 *      `/ats_interviews/new` when a single scorecard is pre-picked.
 *
 *   2. Nested `children: [{ kind: 'scorecard', questions, ... }, ...]`
 *      — the hierarchical plan returned by `/ats_job_stage_actions`
 *      (the plan editor). Prefer the first scorecard child that has
 *      questions, else the first scorecard child of any shape.
 *
 * The component only cares about "which blob do I feed to
 * payloadToFormValues" — this module encapsulates the shape sniffing.
 */

type PlanDict = Record<string, unknown>;

export interface ResolvedPlanScorecard {
  /** The raw blob to seed form values from (may be `undefined` when the plan has nothing usable). */
  raw: PlanDict | undefined;
  /**
   * `scorecard_template` / `template_name` / `name` on the raw blob —
   * used to pick a matching ATS template row after hydrate.
   */
  plannedName?: string;
}

export function resolvePlanScorecardSource(
  plan: PlanDict
): ResolvedPlanScorecard {
  // Three shapes seen in the wild:
  //   - `scorecard_config` (flat, legacy)
  //   - `scorecard` (flat, what `/ats_interviews/new` actually emits
  //     for the scheduling wizard — a singular object with
  //     `questions`, `is_feedback_mandatory`, etc.)
  //   - `children: [{ kind: 'scorecard', ... }, ...]` (nested,
  //     returned by the plan editor `/ats_job_stage_actions`)
  const flatConfig = plan.scorecard_config as PlanDict | undefined;
  const flatScorecard = plan.scorecard as PlanDict | undefined;
  const flat = flatConfig ?? flatScorecard;
  const children = Array.isArray(plan.children)
    ? (plan.children as PlanDict[])
    : [];
  const firstWithQuestions = children.find(
    (c) =>
      c?.kind === 'scorecard' &&
      Array.isArray(c.questions) &&
      (c.questions as unknown[]).length > 0
  );
  const firstScorecardChild = children.find((c) => c?.kind === 'scorecard');

  const raw =
    flat &&
    Array.isArray(flat.questions) &&
    (flat.questions as unknown[]).length > 0
      ? flat
      : (firstWithQuestions ?? firstScorecardChild ?? flat);

  const plannedName = raw
    ? (typeof raw.scorecard_template === 'string' &&
        (raw.scorecard_template as string)) ||
      (typeof raw.template_name === 'string' &&
        (raw.template_name as string)) ||
      (typeof raw.name === 'string' && (raw.name as string)) ||
      undefined
    : undefined;

  return { raw, plannedName: plannedName || undefined };
}
